import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INQUIRY_TYPES = ['Brand', 'Retailer', 'Manufacturer', 'Investor', 'Partnership', 'Other'];

const SLATE = '#0B1426';
const MUTED = '#5B6577';
const HAIRLINE = '#ECECEC';
const CORAL = '#E63946';

/**
 * Premium enterprise inquiry form. Replaces the mailto: workflow that
 * launched a visitor's email client. Submits to /api/inquiries which
 * stores + dispatches to info@tagc.us (never exposed publicly).
 */
export default function InquiryDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', website: '',
    inquiry_type: '', message: '', website_secondary: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const reset = () => {
    setForm({ name: '', company: '', email: '', phone: '', website: '',
      inquiry_type: '', message: '', website_secondary: '' });
    setError(''); setSuccess(null);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.company.trim() || !form.email.trim()
        || !form.inquiry_type || !form.message.trim()) {
      setError('Please complete the required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await axios.post(`${API_URL}/api/inquiries`, {
        ...form,
        source_page: typeof window !== 'undefined' ? window.location.href : null,
      });
      setSuccess(resp.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Submission failed. Please try again.';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-xl max-h-[90vh] overflow-y-auto p-0 gap-0 rounded-lg"
        style={{ backgroundColor: '#FFFFFF', border: `1px solid ${HAIRLINE}` }}
        data-testid="inquiry-dialog"
      >
        <DialogHeader
          className="px-7 pt-7 pb-5 space-y-2"
          style={{ borderBottom: `1px solid ${HAIRLINE}` }}
        >
          <p
            className="text-[10px] font-bold uppercase tracking-[0.3em]"
            style={{ color: CORAL }}
          >
            Partnership Inquiries
          </p>
          <DialogTitle
            className="font-light tracking-tight leading-[1.15]"
            style={{
              color: SLATE,
              fontFamily: '"Cormorant Garamond", "Times New Roman", serif',
              fontSize: '1.8rem',
            }}
          >
            Get in touch with The Agyekum Group.
          </DialogTitle>
          <p className="text-[13px] leading-relaxed" style={{ color: MUTED }}>
            Brands, retailers, manufacturers, and investors — share a few details
            and a member of the team will follow up.
          </p>
        </DialogHeader>

        {success ? (
          <div className="px-7 py-10 text-center" data-testid="inquiry-success">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-5"
              style={{ backgroundColor: '#FBF8F1', border: `1px solid ${HAIRLINE}` }}>
              <span style={{ color: CORAL, fontSize: 20 }}>✓</span>
            </div>
            <p className="text-[15px] leading-relaxed mb-2" style={{ color: SLATE }}>
              Inquiry received. A member of The Agyekum Group team will follow up shortly.
            </p>
            {success.submission_id && (
              <p className="text-[11px] font-mono tracking-wider mt-3" style={{ color: MUTED }}>
                Reference · {success.submission_id}
              </p>
            )}
            <Button
              onClick={() => handleClose(false)}
              className="mt-7 h-10 px-6 rounded-full text-[11px] font-bold uppercase tracking-[0.22em]"
              style={{ backgroundColor: SLATE, color: '#FFFFFF' }}
              data-testid="inquiry-close-success"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 pb-7 pt-6 space-y-5" data-testid="inquiry-form">
            {/* Honeypot — visually hidden, screen-reader hidden, bots fill */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
            >
              <label>
                Website (do not fill)
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website_secondary}
                  onChange={set('website_secondary')}
                />
              </label>
            </div>

            <Row>
              <Field label="Name" required>
                <Input
                  value={form.name} onChange={set('name')} required
                  data-testid="inq-name"
                  className="h-10 rounded-sm"
                />
              </Field>
              <Field label="Company" required>
                <Input
                  value={form.company} onChange={set('company')} required
                  data-testid="inq-company"
                  className="h-10 rounded-sm"
                />
              </Field>
            </Row>

            <Row>
              <Field label="Email" required>
                <Input
                  type="email" value={form.email} onChange={set('email')} required
                  data-testid="inq-email"
                  className="h-10 rounded-sm"
                />
              </Field>
              <Field label="Phone" optional>
                <Input
                  type="tel" value={form.phone} onChange={set('phone')}
                  data-testid="inq-phone"
                  className="h-10 rounded-sm"
                />
              </Field>
            </Row>

            <Row>
              <Field label="Website" optional>
                <Input
                  type="url" value={form.website} onChange={set('website')}
                  placeholder="https://"
                  data-testid="inq-website"
                  className="h-10 rounded-sm"
                />
              </Field>
              <Field label="Inquiry Type" required>
                <Select value={form.inquiry_type} onValueChange={set('inquiry_type')}>
                  <SelectTrigger className="h-10 rounded-sm" data-testid="inq-type">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {INQUIRY_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`inq-type-${t.toLowerCase()}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </Row>

            <Field label="Message" required>
              <Textarea
                rows={5}
                value={form.message} onChange={set('message')} required
                placeholder="Briefly describe your engagement scope, jurisdictions, and the question that prompted the call."
                data-testid="inq-message"
                className="rounded-sm resize-none"
              />
            </Field>

            {error && (
              <p
                className="text-[12px] px-3 py-2 rounded-sm"
                style={{ color: '#9B1C1C', backgroundColor: '#FDE8E8', border: '1px solid #F8B4B4' }}
                data-testid="inquiry-error"
              >
                {error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="inq-submit"
                className="w-full sm:w-auto h-11 px-7 rounded-full text-[11px] font-bold uppercase tracking-[0.22em]"
                style={{ backgroundColor: CORAL, color: '#FFFFFF' }}
              >
                {submitting ? 'Sending…' : 'Send inquiry →'}
              </Button>
              <p className="text-[11px] leading-relaxed text-center sm:text-left" style={{ color: MUTED }}>
                Your details are shared only with The Agyekum Group team.
              </p>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, optional, children }) {
  return (
    <div className="space-y-1.5">
      <Label
        className="text-[10px] font-bold uppercase tracking-[0.22em]"
        style={{ color: MUTED }}
      >
        {label}
        {required && <span style={{ color: CORAL }}> *</span>}
        {optional && <span className="font-normal normal-case tracking-normal text-[11px] ml-1" style={{ color: MUTED }}>(optional)</span>}
      </Label>
      {children}
    </div>
  );
}
